import { useQuery } from '@tanstack/react-query';
import type { AlphaAtlasOutcomeKind, AlphaAtlasSignalDuration } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import AdminLayout from '@/components/Layout/AdminLayout';
import AlphaAtlasValidationResearch from './AlphaAtlasValidationResearch';

const time = (unix: number | null): string =>
    unix === null
        ? '—'
        : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' }).format(
              unix * 1_000
          );

const durationLabel: Record<AlphaAtlasSignalDuration, string> = {
    '1d': 'Daily',
    '240m': '240-minute',
    '30m': '30-minute'
};

const sliceLabel = (run: { durationId: AlphaAtlasSignalDuration; outcomeKind: AlphaAtlasOutcomeKind }): string =>
    `${durationLabel[run.durationId]} · ${run.outcomeKind === 'underlying' ? 'Underlying' : 'Options'}`;

const AlphaAtlasRunHealth = () => {
    const health = useQuery({ ...trpc.alphaAtlas.runHealth.queryOptions({ limit: 25 }), refetchInterval: 30_000 });

    return (
        <AdminLayout containerSize="1600px" title="Alpha Atlas clean-history health">
            <div className="space-y-6 text-slate-100">
                <p className="text-sm text-slate-400">
                    Only sealed clean-history publications are reader-visible. Work units, source calls, analytical writes, and failures
                    stay explicit here.
                </p>
                {health.error && (
                    <p className="rounded border border-red-800 p-4 text-red-300" role="alert">
                        {health.error.message}
                    </p>
                )}
                <div className="space-y-3">
                    {health.data?.map((run) => (
                        <article className="rounded-xl border border-slate-800 bg-slate-900 p-4" key={run.generationId}>
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <strong>
                                    Generation {run.generationId} · {sliceLabel(run)} · {run.generationKind}
                                </strong>
                                <span className="rounded bg-slate-800 px-2 py-1 text-xs uppercase">{run.status}</span>
                            </div>
                            <p className="mt-1 break-all text-xs text-slate-500">{run.generationFingerprint}</p>
                            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                                <HealthFact
                                    label="Work units"
                                    value={`${run.completedWorkCount} complete · ${run.pendingWorkCount} pending · ${run.leasedWorkCount} leased`}
                                />
                                <HealthFact
                                    label="Exceptions"
                                    value={`${run.failedWorkCount} failed · ${run.quarantinedWorkCount} quarantined`}
                                />
                                <HealthFact label="Source requests" value={run.sourceRequestCount.toLocaleString()} />
                                <HealthFact label="ClickHouse rows written" value={run.clickHouseWrittenRowCount.toLocaleString()} />
                                <HealthFact label="As of" value={time(run.asOfAtUnix)} />
                                <HealthFact label="Heartbeat" value={time(run.heartbeatAtUnix)} />
                                <HealthFact label="Completed" value={time(run.completedAtUnix)} />
                                <HealthFact label="Publication" value={run.publicationId?.toString() ?? 'Not sealed'} />
                            </dl>
                            {run.failureCode && <p className="mt-3 text-sm text-red-300">Failure: {run.failureCode}</p>}
                        </article>
                    ))}
                </div>
                <AlphaAtlasValidationResearch />
            </div>
        </AdminLayout>
    );
};

const HealthFact = ({ label, value }: { label: string; value: string }) => (
    <div>
        <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
        <dd className="mt-1 text-slate-300">{value}</dd>
    </div>
);

export default AlphaAtlasRunHealth;
